import { Row, Col } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useRouter } from "next/router";
import { Progress, Button } from "@dreambigger/design-system/src/components";
import {
  FinancialBrand,
  SuccessStepAssets,
  PeopleStepAssets,
} from "@dreambigger/shared/src/types";
import styles from "./StepContentSection.module.scss";
import { useSegment } from "@dreambigger/shared/src/hooks";
import {
  isModernTheme,
  shouldHideHeroImage,
} from "@dreambigger/shared/src/utils";
import { isInAdminMode } from "../AdminApplicationWrapper";
import StepContentSectionV2 from "./StepContentSectionV2";
import StepContentSectionV2NoImage from "./StepContentSectionV2NoImage";
import SuccessContentV1 from "./SuccessContentV1";

interface Props {
  children: any;
  brand: FinancialBrand;
  title: string;
  subTitle?: string;
  description: string;
  stepProgress: number;
  previousSlug?: string;
  stepType?: string;
  assets?: SuccessStepAssets | PeopleStepAssets;
  hideProgressBar?: boolean;
  heroImage?: string;
  heroText?: string;
  heroLogo?: string;
}

const StepContentSection = ({
  children,
  brand,
  title,
  subTitle,
  description,
  stepProgress,
  previousSlug,
  stepType,
  assets,
  hideProgressBar,
  heroImage,
  heroText,
  heroLogo,
}: Props) => {
  const theme = brand.theme || "v1";
  const router = useRouter();
  const { flowId, stepSlug } = router.query;
  const segment = useSegment();

  const handleClick = () => {
    router.push({
      query: {
        ...router.query,
        flowId,
        stepSlug: previousSlug,
      },
    });
    segment.track({
      action: "Button Click",
      label: `Back - ${stepSlug as string}`,
    });
  };

  const isAdminMode = isInAdminMode();

  // Under v3, every step except start/success renders as if no hero image were
  // set, regardless of whether one is configured.
  const effectiveHeroImage = shouldHideHeroImage(theme, stepType)
    ? undefined
    : heroImage;

  // Modern (v2/v3) layout with no heroImage - Use NoImage component.
  if (isModernTheme(theme) && !effectiveHeroImage) {
    return (
      <StepContentSectionV2NoImage
        brand={brand}
        title={title}
        subTitle={subTitle}
        description={description}
        stepProgress={stepProgress}
        previousSlug={previousSlug}
        stepType={stepType}
        assets={assets}
        hideProgressBar={hideProgressBar}
        heroImage={effectiveHeroImage}
        heroText={heroText}
        heroLogo={heroLogo}
      >
        {children}
      </StepContentSectionV2NoImage>
    );
  }

  // Modern (v2/v3) layout - Use separate component
  if (isModernTheme(theme)) {
    return (
      <StepContentSectionV2
        brand={brand}
        title={title}
        subTitle={subTitle}
        description={description}
        stepProgress={stepProgress}
        previousSlug={previousSlug}
        stepType={stepType}
        assets={assets}
        hideProgressBar={hideProgressBar}
        heroImage={effectiveHeroImage}
        heroText={heroText}
        heroLogo={heroLogo}
      >
        {children}
      </StepContentSectionV2>
    );
  }

  // V1 Layout - For success step, use separate component
  if (stepType === "success" && router.query.submission === "complete") {
    return (
      <SuccessContentV1
        brand={brand}
        title={title}
        subTitle={subTitle}
        description={description}
        assets={assets}
        isAdminMode={isAdminMode}
      >
        {children}
      </SuccessContentV1>
    );
  }

  // Default render:
  return (
    <div className={styles.content}>
      {/* Progress Bar */}
      <Row>
        <Col
          className={hideProgressBar ? "" : "lh-2 pt-4 mb-6"}
          span="18"
          lg={12}
        >
          <div className="flex">
            {previousSlug && (
              <Button
                type="primary"
                onClick={handleClick}
                className="self-center mr-4 br-100 ph-2 pv-1 h-6 w-7 s-2 lift"
              >
                <LeftOutlined />
              </Button>
            )}
            {!hideProgressBar && (
              <>
                <span className="mr-2 self-center fwb primary f-4">
                  {stepProgress}%
                </span>
                <Col className="w-100 bl b-primary pl-3">
                  <Progress
                    trailColor="#e3e1e1"
                    strokeColor={brand.primaryColor}
                    percent={stepProgress}
                    showInfo={false}
                  />
                </Col>
              </>
            )}
          </div>
        </Col>
      </Row>
      {/* Step Title */}
      <h1
        className="f-7 sm_f-8 fwt mb-0 lh-1 mb-4"
        style={{ color: brand.stepTitleColor || "black" }}
        dangerouslySetInnerHTML={{
          __html: title ?? "",
        }}
      />
      {/* Step Description */}
      <p
        className="f-4 gray-8 mb-5 lh-3"
        dangerouslySetInnerHTML={{
          __html: description ?? "",
        }}
      />
      {children}
    </div>
  );
};

export default StepContentSection;
